import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Search, Check, X, Clock, IndianRupee, User, Building2, Briefcase, Calendar, Pencil, Trash2, Ban, CheckCircle2 } from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  eligible: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-violet-100 text-violet-700',
  cancelled: 'bg-muted text-muted-foreground',
};

const INITIAL_FORM = {
  candidate_name: '',
  client_id: '',
  client_name: '',
  date_of_joining: '',
  position: '',
  ctc: '',
  incentive_amount: '',
  remarks: '',
};

export default function RecruiterJoinersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'accounts';
  
  const [joiners, setJoiners] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(isAdmin ? 'all' : 'my');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isAdmin ? '/recruiter-joiners' : '/recruiter-joiners/my-joiners';
      const [joinersRes, clientsRes] = await Promise.all([
        api.get(endpoint),
        api.get('/finance/clients').catch(() => ({ data: [] })),
      ]);
      setJoiners(joinersRes.data);
      setClients(clientsRes.data || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => joiners.filter(j => {
    const matchSearch = j.candidate_name?.toLowerCase().includes(search.toLowerCase()) ||
                        j.client_name?.toLowerCase().includes(search.toLowerCase()) ||
                        j.recruiter_name?.toLowerCase().includes(search.toLowerCase());
    
    if (activeTab === 'pending') return matchSearch && j.status === 'pending';
    if (activeTab === 'approved') return matchSearch && j.status === 'approved';
    if (activeTab === 'eligible') return matchSearch && j.status === 'eligible';
    if (activeTab === 'paid') return matchSearch && j.status === 'paid';
    return matchSearch;
  }), [joiners, search, activeTab]);

  const openAdd = () => {
    setForm(INITIAL_FORM);
    setEditMode(false);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (joiner) => {
    setForm({
      candidate_name: joiner.candidate_name,
      client_id: joiner.client_id || '',
      client_name: joiner.client_name,
      date_of_joining: joiner.date_of_joining,
      position: joiner.position,
      ctc: joiner.ctc.toString(),
      incentive_amount: joiner.incentive_amount.toString(),
      remarks: joiner.remarks || '',
    });
    setEditMode(true);
    setEditingId(joiner.id);
    setDialogOpen(true);
  };

  const handleClientSelect = (clientId) => {
    if (clientId === 'other') {
      setForm(prev => ({ ...prev, client_id: '', client_name: '' }));
    } else {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setForm(prev => ({
          ...prev,
          client_id: clientId,
          client_name: client.client_name,
        }));
      }
    }
  };

  const handleSave = async () => {
    if (!form.candidate_name || !form.client_name || !form.date_of_joining || !form.position || !form.ctc || !form.incentive_amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        ctc: parseFloat(form.ctc),
        incentive_amount: parseFloat(form.incentive_amount),
        client_id: form.client_id || null,
      };
      
      if (editMode) {
        await api.put(`/recruiter-joiners/${editingId}`, payload);
        toast.success('Joiner entry updated');
      } else {
        await api.post('/recruiter-joiners', payload);
        toast.success('Joiner entry submitted for approval');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const approveJoiner = async (id) => {
    try {
      await api.put(`/recruiter-joiners/${id}/approve`);
      toast.success('Joiner approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const openRejectDialog = (id) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const rejectJoiner = async () => {
    try {
      await api.put(`/recruiter-joiners/${rejectingId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Joiner rejected');
      setRejectDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const markAsPaid = async (id) => {
    const month = new Date().toISOString().slice(0, 7);
    try {
      await api.put(`/recruiter-joiners/${id}/mark-paid?payroll_month=${month}`);
      toast.success('Marked as paid');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const cancelJoiner = async (id) => {
    if (!window.confirm('Cancel this joiner entry? This is typically done when candidate leaves before 90 days.')) return;
    try {
      await api.put(`/recruiter-joiners/${id}/cancel`);
      toast.success('Joiner cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const deleteJoiner = async (id) => {
    if (!window.confirm('Delete this joiner entry?')) return;
    try {
      await api.delete(`/recruiter-joiners/${id}`);
      toast.success('Joiner deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Calculate summary stats
  const pendingCount = joiners.filter(j => j.status === 'pending').length;
  const eligibleCount = joiners.filter(j => j.status === 'eligible').length;
  const eligibleAmount = joiners.filter(j => j.status === 'eligible').reduce((sum, j) => sum + j.incentive_amount, 0);

  return (
    <div data-testid="recruiter-joiners-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">
            {isAdmin ? 'Recruiter Joiners & Incentives' : 'My Joiners'}
          </h2>
          <p className="text-sm text-muted-foreground font-body mt-1">
            {isAdmin ? 'Review and approve joiner entries, track incentive eligibility' : 'Submit your candidate joiners for incentive calculation'}
          </p>
        </div>
        <Button onClick={openAdd} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="add-joiner-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Joiner
        </Button>
      </div>

      {/* Summary Cards for Admin */}
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-sm p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest">Pending Approval</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-sm p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest">Eligible for Payroll</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{eligibleCount}</p>
          </div>
          <div className="bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800 rounded-sm p-4">
            <div className="flex items-center gap-2 text-violet-600 mb-1">
              <IndianRupee className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest">Eligible Amount</span>
            </div>
            <p className="text-2xl font-bold text-violet-700">{formatCurrency(eligibleAmount)}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          {isAdmin && <TabsTrigger value="pending">Pending ({pendingCount})</TabsTrigger>}
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="eligible">Eligible</TabsTrigger>
          <TabsTrigger value="paid">Paid</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search joiners..." className="pl-10 rounded-sm" />
        </div>
      </div>

      {/* Table */}
      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Candidate</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Position</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">DOJ</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">CTC</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Incentive</TableHead>
              {isAdmin && <TableHead className="text-xs uppercase tracking-widest">Recruiter</TableHead>}
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">90 Days</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-8 text-muted-foreground">No joiners found</TableCell></TableRow>
            ) : filtered.map(j => (
              <TableRow key={j.id} data-testid={`joiner-row-${j.id}`}>
                <TableCell className="font-medium">{j.candidate_name}</TableCell>
                <TableCell>{j.client_name}</TableCell>
                <TableCell>{j.position}</TableCell>
                <TableCell className="text-sm">{j.date_of_joining}</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(j.ctc)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-600 font-medium">{formatCurrency(j.incentive_amount)}</TableCell>
                {isAdmin && <TableCell>{j.recruiter_name}</TableCell>}
                <TableCell>
                  <Badge className={STATUS_COLORS[j.status]}>{j.status}</Badge>
                </TableCell>
                <TableCell>
                  {j.status === 'approved' && j.days_until_eligible !== undefined && (
                    <span className={`text-xs ${j.is_eligible ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`}>
                      {j.is_eligible ? '✓ Eligible' : `${j.days_until_eligible} days left`}
                    </span>
                  )}
                  {j.status === 'eligible' && <span className="text-xs text-emerald-600 font-medium">✓ Ready</span>}
                  {j.status === 'paid' && <span className="text-xs text-violet-600 font-medium">✓ Paid</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {/* Admin actions for pending */}
                    {isAdmin && j.status === 'pending' && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => approveJoiner(j.id)} className="text-emerald-600" title="Approve">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openRejectDialog(j.id)} className="text-red-600" title="Reject">
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    
                    {/* Mark as paid for eligible */}
                    {isAdmin && j.status === 'eligible' && (
                      <Button variant="ghost" size="sm" onClick={() => markAsPaid(j.id)} className="text-violet-600" title="Mark as Paid">
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {/* Cancel (for approved entries - candidate left) */}
                    {isAdmin && ['approved', 'eligible'].includes(j.status) && (
                      <Button variant="ghost" size="sm" onClick={() => cancelJoiner(j.id)} className="text-amber-600" title="Cancel (Candidate Left)">
                        <Ban className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {/* Edit - only pending entries */}
                    {j.status === 'pending' && (isAdmin || j.recruiter_id === user?.id) && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(j)} className="text-blue-600" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {/* Delete - only pending entries */}
                    {j.status === 'pending' && (isAdmin || j.recruiter_id === user?.id) && (
                      <Button variant="ghost" size="sm" onClick={() => deleteJoiner(j.id)} className="text-red-600" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Joiner Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editMode ? 'Edit Joiner Entry' : 'Add New Joiner'}</DialogTitle>
            <DialogDescription>Enter candidate details for incentive calculation. A placement will be created upon approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                <User className="w-3 h-3" /> Candidate Name *
              </Label>
              <Input value={form.candidate_name} onChange={e => set('candidate_name', e.target.value)} className="rounded-sm" placeholder="Full name of the candidate" />
            </div>

            {/* Client Selection */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Client *
              </Label>
              {clients.length > 0 ? (
                <Select value={form.client_id || 'other'} onValueChange={handleClientSelect}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select client or enter new" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.filter(c => c.is_active).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.client_name}{c.state ? ` - ${c.state}` : ''}</SelectItem>
                    ))}
                    <SelectItem value="other">+ Enter New Client</SelectItem>
                  </SelectContent>
                </Select>
              ) : null}
              {(!form.client_id || clients.length === 0) && (
                <Input 
                  value={form.client_name} 
                  onChange={e => set('client_name', e.target.value)} 
                  className="rounded-sm mt-2" 
                  placeholder="Enter client name" 
                />
              )}
              <p className="text-xs text-muted-foreground">Select existing client or enter new name (will be created on approval)</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Date of Joining *
                </Label>
                <Input type="date" value={form.date_of_joining} onChange={e => set('date_of_joining', e.target.value)} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> Position/Role *
                </Label>
                <Input value={form.position} onChange={e => set('position', e.target.value)} className="rounded-sm" placeholder="e.g., Software Engineer" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">CTC (Annual) *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" value={form.ctc} onChange={e => set('ctc', e.target.value)} className="pl-10 rounded-sm font-mono" placeholder="e.g., 1200000" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Incentive Amount *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                  <Input type="number" value={form.incentive_amount} onChange={e => set('incentive_amount', e.target.value)} className="pl-10 rounded-sm font-mono border-emerald-200 focus:border-emerald-500" placeholder="e.g., 10000" />
                </div>
                <p className="text-xs text-muted-foreground">Payable after 90 days from DOJ</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Remarks (Optional)</Label>
              <Textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} className="rounded-sm" rows={2} placeholder="Any additional notes..." />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 rounded-sm">
              {saving ? 'Saving...' : editMode ? 'Update' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Reject Joiner Entry</DialogTitle>
            <DialogDescription>Please provide a reason for rejection</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea 
              value={rejectReason} 
              onChange={e => setRejectReason(e.target.value)} 
              className="rounded-sm" 
              rows={3} 
              placeholder="Reason for rejection (optional)" 
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={rejectJoiner} className="bg-red-600 hover:bg-red-700 rounded-sm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
